/**
 * 「どの時点の姿を見ているか」。このシステムの核なので、画面全体で1つだけ持つ。
 *
 * 2つの軸がある:
 *
 * 1. **knownAt（知識時点）** — セッションを通して固定する。
 *    RegisterEntryVersion は追記専用で recordedAt は DB の実時刻なので、
 *    画面ごとに knownAt を取り直すと、閲覧している最中に誰かが追記したときに
 *    **一覧には無いのに履歴には出る行**（あるいはその逆）が生まれる。
 *    そこで最初に受け取った knownAt を保持し、以降のリクエストに必ず載せる。
 *    申請パッケージの出力（機能32）も同じ knowledgeTime 固定に乗るので、仕組みを二重に作らない。
 *
 * 2. **asOf（基準日）** — 利用者が「2027-01-31 時点に戻して見る」と選んだ日付。
 *    準備度・評価基準・台帳の一覧がこの日付で読む。null なら今日（最新）。
 *
 * 画面上部の「時点の帯」は、過去を見ているとき（asOf を選んだとき、または
 * 固定した knownAt が古くなったとき）だけ出す。最新を見ているときは何も出さない。
 */
import { todayJst } from "./jst.js";

/** 固定した knownAt がこれより古くなったら「過去を見ている」として帯を出す。 */
export const STALE_AFTER_MS = 15 * 60_000;

export interface ViewpointState {
  /** 固定中の知識時点。未固定なら null（サーバが現在時刻を使う）。 */
  knownAt: string | null;
  /** 利用者が選んだ基準日（YYYY-MM-DD）。null なら今日。 */
  asOf: string | null;
}

type Listener = (s: ViewpointState) => void;

let state: ViewpointState = { knownAt: null, asOf: null };
const listeners = new Set<Listener>();
// reset だけを知らせる別チャンネル。pinIfUnset（初回の自動固定）と区別するためにある。
// UX H-01: 自分の書き込み成功後や「最新に戻る」の後に各画面の一覧を読み直すのは
// 「固定が外れた」ことそのものに反応したい。pinIfUnset のたびに毎回全画面を読み直すと、
// 開いたばかりの画面まで無駄に再読込される。
const resetListeners = new Set<() => void>();

function set(next: Partial<ViewpointState>): void {
  state = { ...state, ...next };
  for (const fn of listeners) fn(state);
}

export const viewpoint = {
  get knownAt(): string | null { return state.knownAt; },
  get asOf(): string | null { return state.asOf; },
  get state(): ViewpointState { return state; },

  /** 読みに使う基準日。選ばれていなければ今日（日本時間）。 */
  effectiveAsOf(): string { return state.asOf ?? todayJst(); },

  /** サーバが実際に使った knownAt を受け取って固定する。最初の1回だけ効く。 */
  pinIfUnset(knownAt: string): void {
    if (state.knownAt === null) set({ knownAt });
  },

  /**
   * 知識時点の固定だけを外す（自分の書き込み成功後に api.ts が呼ぶ）。
   * 利用者が選んだ基準日（asOf）は触らない。各画面は subscribeReset で一覧を読み直す。
   */
  resetKnownAt(): void {
    set({ knownAt: null });
    for (const fn of resetListeners) fn();
  },

  /** 「最新に戻る」。基準日も知識時点も外し、各画面に読み直させる。 */
  reset(): void {
    set({ knownAt: null, asOf: null });
    for (const fn of resetListeners) fn();
  },

  /** 基準日を選ぶ。今日を選んだら「最新」と同じ扱い（帯を出さない）。 */
  setAsOf(date: string | null): void {
    const v = date && date !== todayJst() ? date : null;
    if (v === state.asOf) return;
    set({ asOf: v });
  },

  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  },

  /** reset のときだけ呼ばれる。画面側の「一覧を読み直す」はここに繋ぐ。 */
  subscribeReset(fn: () => void): () => void {
    resetListeners.add(fn);
    return () => { resetListeners.delete(fn); };
  },
};

/** 過去を見ているか（帯を出すか）。now は呼び出し側の時計（毎分更新）。 */
export function pastViewpoint(s: ViewpointState, now: number): { kind: "asOf"; date: string } | { kind: "knownAt"; at: string } | null {
  if (s.asOf) return { kind: "asOf", date: s.asOf };
  if (s.knownAt && now - new Date(s.knownAt).getTime() > STALE_AFTER_MS) return { kind: "knownAt", at: s.knownAt };
  return null;
}
